import { Link } from "react-router-dom";
import { ArrowUpRight, Phone } from "lucide-react";
import ServiceCard from "@/components/ServiceCard";
import useSeo from "@/hooks/useSeo";
import { SERVICES, PHONE_DISPLAY, PHONE_TEL } from "@/lib/data";

const GROUPS = [
  { title: "Build & Renovate", slugs: ["renovations", "carpentry", "painting", "roofing"], blurb: "Heavy lifting jobs that change how the property looks and works." },
  { title: "Repairs & Maintenance", slugs: ["maintenance", "handyman", "general-repairs", "cleaning"], blurb: "The stuff that keeps a property running, day to day." },
  { title: "Trade Specialities", slugs: ["electrical", "plumbing", "locksmithing", "cctv-antennas-cabling"], blurb: "Licensed work, done safely, signed off properly." },
  { title: "Outdoor & Property", slugs: ["landscaping", "pool-services"], blurb: "Outside the house — gardens, pools, the works." },
];

export default function Services() {
  useSeo({
    title: "Services — RSR Australia",
    description: "Renovations, plumbing, electrical, roofing, painting, carpentry, cleaning and more — 14 trades, one phone number. Australia-wide.",
  });
  return (
    <>
      <section data-testid="services-hero" className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 pt-20 pb-16 lg:pt-28 lg:pb-24 grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 lg:col-span-8">
            <p className="overline">Services</p>
            <h1 className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.98] font-extrabold">
              Every job your <br className="hidden sm:block" /> property might <span className="text-accent">ever need.</span>
            </h1>
          </div>
          <div className="col-span-12 lg:col-span-4">
            <p className="text-base text-muted-foreground">
              One call covers nearly every property job. Tap any service to see what's included — or grab a quick quote directly from any card.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="services-hero-quote" className="inline-flex items-center gap-2 bg-accent text-white px-5 py-3 text-sm font-semibold hover:bg-accent/90 transition-colors">
                Get a free quote <ArrowUpRight className="w-4 h-4" />
              </Link>
              <a href={`tel:${PHONE_TEL}`} data-testid="services-hero-call" className="inline-flex items-center gap-2 border border-border hover:border-foreground/40 px-5 py-3 text-sm font-semibold transition-colors">
                <Phone className="w-4 h-4" /> {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        </div>
      </section>

      {GROUPS.map((group, gi) => (
        <section key={group.title} data-testid={`service-group-${gi}`} className={`py-16 sm:py-20 ${gi % 2 === 1 ? "bg-card border-y border-border" : ""}`}>
          <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
            <div className="grid grid-cols-12 gap-8 items-end mb-10">
              <div className="col-span-12 lg:col-span-7 flex items-center gap-5 flex-wrap">
                <span className="font-display text-3xl text-accent font-extrabold tabular-nums">{String(gi + 1).padStart(2, "0")}</span>
                <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-extrabold">{group.title}</h2>
              </div>
              <div className="col-span-12 lg:col-span-4 lg:col-start-9 text-sm text-muted-foreground">{group.blurb}</div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-border">
              {group.slugs.map((slug) => {
                const s = SERVICES.find(x => x.slug === slug);
                if (!s) return null;
                return <ServiceCard key={s.slug} service={s} testIdPrefix="services-page-card" />;
              })}
            </div>
          </div>
        </section>
      ))}

      {/* Bottom CTA */}
      <section className="py-20">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
          <div className="border border-border bg-card p-8 lg:p-14 flex flex-col lg:flex-row gap-8 items-start lg:items-center justify-between">
            <div>
              <p className="overline">Not sure which service?</p>
              <h3 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tighter">Just call. We'll work it out.</h3>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <a href={`tel:${PHONE_TEL}`} data-testid="services-bottom-call" className="inline-flex items-center gap-3 bg-accent text-white px-6 py-4 font-semibold text-sm hover:bg-accent/90 transition-colors">
                <Phone className="w-4 h-4" /> {PHONE_DISPLAY}
              </a>
              <Link to="/contact" className="inline-flex items-center gap-3 border border-border hover:border-foreground/40 px-6 py-4 font-semibold text-sm transition-colors">
                Send a job brief <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
